import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../state";
import { LanguageSwitcher } from "./language-switcher";

/**
 * Balance shown in the navbar. Pages that refresh the balance themselves
 * (the dashboard) pass their live state; everywhere else the cached
 * profile balance is used.
 */
export type NavBalance = number | "loading" | "error";

function BalanceLabel({ balance }: { balance: NavBalance }) {
  const { t } = useTranslation();
  if (balance === "loading") {
    return (
      <>
        <span className="balance-loading">…</span> {t("common.buxUnit")}
      </>
    );
  }
  if (balance === "error") return <>— {t("common.buxUnit")}</>;
  return <>{t("common.bux", { amount: balance })}</>;
}

export function Navbar({ balance }: { balance?: NavBalance }) {
  const { t } = useTranslation();
  const { currentNinja, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const name = currentNinja?.firstName ?? "";
  const shownBalance = balance ?? currentNinja?.currentBalance ?? 0;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `nav-link ${isActive ? "active" : ""}`;

  return (
    <nav className="navbar" aria-label={t("nav.primaryNavigation")}>
      <div className="nav-container">
        <div className="nav-left">
          <img src="/CodeNinjasLogo.svg" alt="Code Ninjas" className="app-logo" />
          <span className="app-title">{t("common.appName")}</span>
        </div>

        <button
          type="button"
          className="nav-toggle"
          aria-controls="primary-navigation"
          aria-expanded={menuOpen}
          aria-label={t("nav.menu")}
          onClick={() => setMenuOpen((isOpen) => !isOpen)}
        >
          <span className="nav-toggle-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="nav-toggle-label">{t("nav.menu")}</span>
        </button>

        <div
          id="primary-navigation"
          className={`nav-menu ${menuOpen ? "is-open" : ""}`}
        >
          <div className="navbar-links">
            <NavLink
              to="/dashboard"
              className={linkClass}
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.dashboard")}
            </NavLink>
            <NavLink
              to="/shop"
              className={linkClass}
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.shop")}
            </NavLink>
            <NavLink
              to="/leaderboard"
              className={linkClass}
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.leaderboard")}
            </NavLink>
          </div>

          <div className="navbar-right">
            <span className="nav-balance">
              <BalanceLabel balance={shownBalance} />
            </span>
            <span className="nav-user" title={name}>
              {name}
            </span>
            <LanguageSwitcher />
            <button
              type="button"
              className="btn btn-logout"
              onClick={() => {
                logout();
                navigate("/");
              }}
            >
              {t("nav.logout")}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
